import React, { useEffect, useRef, useState } from "react";
import { geoOrthographic, geoPath, geoGraticule10, geoInterpolate, geoDistance } from "d3-geo";
import { feature } from "topojson-client";
import land110 from "world-atlas/land-110m.json";
import { Card } from "./TvBits";

const LAND = feature(land110, land110.objects.land);

export const TvGlobe = ({ d }) => {
  const ref = useRef(null);
  const pins = [
    ...(d.world || []).filter((w) => w.lat != null).map((w) => ({ ...w, kind: "news" })),
    ...(d.quakes || []).filter((q) => q.mag >= 5).slice(0, 4).map((q) => ({ title: `M${q.mag} earthquake · ${q.place}`, place: "Quake", lat: q.lat, lon: q.lon, kind: "quake" })),
  ];
  const [i, setI] = useState(0);
  const cur = pins.length ? pins[i % pins.length] : null;
  const rotRef = useRef([-20, -20]);
  const pinsRef = useRef(pins); pinsRef.current = pins;
  const curRef = useRef(cur); curRef.current = cur;
  useEffect(() => { const t = setInterval(() => setI((x) => x + 1), 7000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const cv = ref.current; const ctx = cv.getContext("2d");
    let raf, last = 0;
    const draw = (ts) => {
      raf = requestAnimationFrame(draw);
      if (ts - last < 50) return; last = ts;
      const W = (cv.width = cv.offsetWidth), H = (cv.height = cv.offsetHeight), R = Math.min(W, H) / 2 - 10;
      const c = curRef.current;
      if (c) rotRef.current = geoInterpolate(rotRef.current, [-c.lon, -c.lat])(0.06);
      const proj = geoOrthographic().scale(R).translate([W / 2, H / 2]).rotate(rotRef.current).clipAngle(90);
      const path = geoPath(proj, ctx);
      ctx.clearRect(0, 0, W, H);
      ctx.beginPath(); path({ type: "Sphere" }); ctx.fillStyle = "#04121f"; ctx.fill(); ctx.strokeStyle = "#00F0FF55"; ctx.lineWidth = 2; ctx.stroke();
      ctx.beginPath(); path(geoGraticule10()); ctx.strokeStyle = "#00F0FF14"; ctx.lineWidth = 1; ctx.stroke();
      ctx.beginPath(); path(LAND); ctx.fillStyle = "#0f3a4a"; ctx.fill(); ctx.strokeStyle = "#22D3EE66"; ctx.stroke();
      const pulse = (Math.sin(ts / 300) + 1) / 2;
      for (const p of pinsRef.current) {
        const r = rotRef.current;
        if (geoDistance([p.lon, p.lat], [-r[0], -r[1]]) > 1.5) continue;
        const xy = proj([p.lon, p.lat]);
        const hot = p === c; const col = p.kind === "quake" ? "#EF4444" : "#F59E0B";
        ctx.beginPath(); ctx.arc(xy[0], xy[1], hot ? 10 + pulse * 14 : 6, 0, 6.28); ctx.fillStyle = col + (hot ? "55" : "aa"); ctx.fill();
        ctx.beginPath(); ctx.arc(xy[0], xy[1], hot ? 8 : 4, 0, 6.28); ctx.fillStyle = col; ctx.fill();
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div className="grid grid-cols-5 gap-5 h-full" data-testid="tv-scene-globe">
      <div className="col-span-3 relative">
        <canvas ref={ref} className="w-full h-full" data-testid="tv-globe-canvas" />
        <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full pointer-events-none nx-spin-slow" style={{ opacity: 0.55 }}>
          <circle cx="50" cy="50" r="49" fill="none" stroke="#00F0FF" strokeWidth="0.25" strokeDasharray="0.6 1.8" />
          <path d="M50 0.5 A49.5 49.5 0 0 1 99.5 50" fill="none" stroke="#F59E0B" strokeWidth="0.5" />
        </svg>
        <div className="absolute top-2 left-2 nx-mono text-sm tracking-widest text-cyan-300/60">GEO·INTEL ▸ {pins.length} EVENTS TRACKED</div>
        {cur && (
          <div key={cur.title} className="absolute left-4 right-4 bottom-4 nx-glass rounded-2xl p-5 nx-fade-up" data-testid="tv-globe-focus">
            <p className="nx-orbitron text-lg tracking-widest font-bold" style={{ color: cur.kind === "quake" ? "#EF4444" : "#F59E0B" }}>{(cur.place || "").toUpperCase()}</p>
            <p className="text-3xl font-bold text-white leading-snug">{cur.title}</p>
          </div>
        )}
      </div>
      <Card title="WORLD NOW" accent="#F59E0B" className="col-span-2" testid="tv-world-list">
        <div className="space-y-5">
          {(d.world || []).slice(0, 6).map((w, k) => (
            <p key={k} className={`text-2xl leading-snug line-clamp-2 ${cur && w.title === cur.title ? "text-amber-200 font-bold" : "text-cyan-50"}`}>
              {w.place && <span className="text-amber-400/80 nx-mono text-lg mr-2">{w.place}</span>}{w.title}
            </p>
          ))}
        </div>
      </Card>
    </div>
  );
};
